import { useEffect, useRef, useState } from "react";
import type { ReactNode } from "react";

/**
 * Landing-redesign animation utilities (Claude Design handoff, May 2026).
 *
 * Four exports, scoped to the landing page redesign:
 *  - `useReveal()`              — IntersectionObserver hook that adds `.in`
 *                                 to the ref'd element when it scrolls into
 *                                 view. Pairs with the `.landing-reveal`
 *                                 CSS utility in `theme.css`.
 *  - `CountUp`                  — eased count-up. Renders `to` on first
 *                                 paint (so prerendered HTML carries the real
 *                                 figure), resets to 0 on the client and
 *                                 counts up once the element enters the
 *                                 viewport. Drives the `$54,000` CTA figure.
 *  - `HighlightUnderline`       — wraps inline text with a semi-transparent
 *                                 accent bar that draws in left-to-right via
 *                                 the `type-on` keyframe. Used for the
 *                                 "taking" (rose) and "harder it is to deny
 *                                 you" (emerald) emphasis lines.
 *  - `FloatingClaims`           — ambient drifting claim-receipt chips
 *                                 layered behind the hero. Pre-seeded with
 *                                 8 chips, staggered start, varying durations
 *                                 + drift-x offsets.
 */

// ---------------------------------------------------------------------------
// useReveal — scroll-into-view trigger for the `.landing-reveal` CSS class
// ---------------------------------------------------------------------------

/**
 * Returns a ref to attach to the element that should animate in. Once the
 * element crosses an 18% intersection threshold, the `in` class is added,
 * which transitions the `.landing-reveal` CSS utility from hidden to
 * visible. One-shot — the observer disconnects after firing.
 */
export function useReveal<T extends HTMLElement>() {
  const ref = useRef<T>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("in");
          }
        });
      },
      { threshold: 0.18 },
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);
  return ref;
}

// ---------------------------------------------------------------------------
// CountUp — eased count-up that starts on scroll-into-view
// ---------------------------------------------------------------------------

interface CountUpProps {
  to: number;
  duration?: number;
  prefix?: string;
  suffix?: string;
  className?: string;
  style?: React.CSSProperties;
}

/**
 * Renders `to` on first paint so the prerendered HTML carries the real figure;
 * on the client resets to 0 and counts up to `to` in `duration` ms (default
 * 1800), ease-out-cubic, once the element crosses a 50% intersection threshold.
 * One-shot, never replays. Formats the integer in en-US (e.g. `54,000`) so the
 * build machine's locale cannot change it.
 */
export function CountUp({
  to,
  duration = 1800,
  prefix = "",
  suffix = "",
  className,
  style,
}: CountUpProps) {
  const ref = useRef<HTMLSpanElement>(null);
  /* First paint is the final figure, so the prerendered HTML carries the
     real number (crawlers and answer engines used to read "$0"). The client
     effect below resets to 0 and the count-up runs on intersection. */
  const [value, setValue] = useState(to);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    /* Effects never run in the static render, so the prerendered HTML keeps
       the final figure. In the browser this runs while the card is still at
       opacity 0 (landing-reveal), so the reset to 0 is never seen. */
    setValue(0);
    let started = false;
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !started) {
            started = true;
            const t0 = performance.now();
            const step = (t: number) => {
              const k = Math.min(1, (t - t0) / duration);
              const eased = 1 - Math.pow(1 - k, 3);
              setValue(Math.round(eased * to));
              if (k < 1) requestAnimationFrame(step);
            };
            requestAnimationFrame(step);
          }
        });
      },
      { threshold: 0.5 },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [to, duration]);

  return (
    <span ref={ref} className={className} style={style}>
      {prefix}
      {value.toLocaleString("en-US")}
      {suffix}
    </span>
  );
}

// ---------------------------------------------------------------------------
// HighlightUnderline — marker-style underline that draws in left-to-right
// ---------------------------------------------------------------------------

type HighlightColor = "amber" | "sky" | "emerald" | "rose";

interface HighlightUnderlineProps {
  color: HighlightColor;
  children: ReactNode;
}

/**
 * Wraps inline text with a marker-style underline. The underline is a
 * semi-transparent accent bar (`opacity: 0.32`) absolutely positioned
 * behind the text, with `width` animating from 0 to 100% via the
 * `type-on` keyframe (0.9s with a 0.5s delay so the bar lands after the
 * text reveal).
 *
 * Color tokens read from `--accent-{color}-ink` (defined in theme.css).
 */
export function HighlightUnderline({
  color,
  children,
}: HighlightUnderlineProps) {
  const ink = `hsl(var(--accent-${color}-ink))`;
  return (
    <span style={{ position: "relative", display: "inline-block" }}>
      <span
        aria-hidden="true"
        style={{
          position: "absolute",
          left: "-2%",
          right: "-2%",
          // Sit the bar lower — top of the band lands around the bottom
          // quarter of the cap-height instead of crossing the middle of
          // the letter. Shorter + below baseline reads as a marker
          // underline, not a strikethrough.
          bottom: -2,
          height: "0.28em",
          borderRadius: 2,
          background: ink,
          opacity: 0.32,
          transformOrigin: "left center",
          animation: "type-on 0.9s 0.5s cubic-bezier(0.65, 0, 0.35, 1) both",
          zIndex: 0,
        }}
      />
      <span style={{ position: "relative", zIndex: 1 }}>{children}</span>
    </span>
  );
}

// ---------------------------------------------------------------------------
// FloatingClaims — ambient drifting claim-receipt chips
// ---------------------------------------------------------------------------

interface ClaimTicker {
  id: string;
  payer: string;
  amount: string;
  state: string;
  accent: HighlightColor;
}

const CLAIM_TICKERS: ClaimTicker[] = [
  {
    id: "A-4872",
    payer: "BCBS",
    amount: "+$1,240",
    state: "PAID",
    accent: "rose",
  },
  {
    id: "B-9120",
    payer: "AETNA",
    amount: "+$386",
    state: "APPEAL WON",
    accent: "amber",
  },
  {
    id: "C-3308",
    payer: "UHC",
    amount: "+$2,144",
    state: "PAID",
    accent: "sky",
  },
  {
    id: "D-7711",
    payer: "CIGNA",
    amount: "+$92",
    state: "PAID",
    accent: "emerald",
  },
  {
    id: "E-2241",
    payer: "ANTHEM",
    amount: "+$1,820",
    state: "RECOVERED",
    accent: "rose",
  },
  {
    id: "F-5560",
    payer: "HUMANA",
    amount: "+$478",
    state: "PAID",
    accent: "amber",
  },
  {
    id: "G-9019",
    payer: "MEDICARE",
    amount: "+$3,012",
    state: "PAID",
    accent: "sky",
  },
  {
    id: "H-1144",
    payer: "BCBS",
    amount: "+$642",
    state: "APPEAL WON",
    accent: "emerald",
  },
];

interface ClaimChipProps {
  item: {
    id: string;
    payer: string;
    amount: string;
    state: string;
    accent: HighlightColor;
    x: number;
    delay: number;
    dur: number;
    driftX: number;
  };
}

function ClaimChip({ item }: ClaimChipProps) {
  const ink = `hsl(var(--accent-${item.accent}-ink))`;
  const tint = `hsl(var(--accent-${item.accent}-tint))`;
  return (
    <div
      className="font-mono"
      style={
        {
          position: "absolute",
          left: `${item.x}%`,
          bottom: "-10vh",
          animation: `drift-up ${item.dur}s linear ${item.delay}s infinite`,
          "--drift-x": `${item.driftX}px`,
          display: "flex",
          alignItems: "center",
          gap: 8,
          padding: "6px 10px",
          borderRadius: 8,
          background: tint,
          color: ink,
          fontSize: 11,
          letterSpacing: "0.04em",
          boxShadow: "0 4px 16px hsl(var(--foreground) / 0.05)",
          border: "1px solid hsl(var(--foreground) / 0.04)",
          whiteSpace: "nowrap",
        } as React.CSSProperties
      }
    >
      <span style={{ opacity: 0.7 }}>#{item.id}</span>
      <span
        aria-hidden
        style={{
          width: 1,
          height: 10,
          background: "currentColor",
          opacity: 0.25,
        }}
      />
      <span style={{ fontWeight: 600 }}>{item.payer}</span>
      <span
        aria-hidden
        style={{
          width: 1,
          height: 10,
          background: "currentColor",
          opacity: 0.25,
        }}
      />
      <span style={{ opacity: 0.85 }}>{item.state}</span>
      <span style={{ fontWeight: 700 }}>{item.amount}</span>
    </div>
  );
}

/**
 * Ambient drifting claim-receipt chips that rise from the bottom of the
 * hero section and fade out as they cross the top. Pure decorative layer
 * — `pointer-events: none` so it never intercepts a click on the H1,
 * the video, or the chevron. Top/bottom mask softens the entry/exit.
 *
 * The 8 chips are pre-seeded with staggered start delays (so they don't
 * release in a single burst), varying durations (22-34s), and drift-x
 * offsets that gently push them off-axis as they rise.
 */
export function FloatingClaims() {
  const [items] = useState(() =>
    CLAIM_TICKERS.map((t, i) => ({
      ...t,
      key: `${t.id}-${i}`,
      x: 8 + ((i * 11) % 84),
      delay: i * 2.6,
      dur: 22 + (i % 4) * 4,
      driftX: (i % 2 === 0 ? 1 : -1) * (10 + i * 3),
    })),
  );

  return (
    <div
      aria-hidden="true"
      style={{
        position: "absolute",
        inset: 0,
        pointerEvents: "none",
        overflow: "hidden",
        WebkitMaskImage:
          "linear-gradient(to bottom, transparent 0, black 12%, black 88%, transparent 100%)",
        maskImage:
          "linear-gradient(to bottom, transparent 0, black 12%, black 88%, transparent 100%)",
      }}
    >
      {items.map((it) => (
        <ClaimChip key={it.key} item={it} />
      ))}
    </div>
  );
}
